import dayjs from 'dayjs';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import {DropdownCustom} from '@ui/molecules/dropdown-custom';
import {Calendar} from '@ui/molecules/calendar';
import {
  ControlButton,
  ControlWrap,
  DateCalendarMenu,
  DateInputButton,
  DatePopover,
  DateSelectField,
  FieldGrid,
  PopoverBody,
  PopoverFooter,
  PopoverHeader,
  SmallPrimaryButton,
  TextButton,
} from '@pages/dashboard/goals/my-objective/my-goals-v2.styles';
import {
  FeedbackFilterButton,
  FeedbackFilterEmpty,
  FeedbackFilterMenu,
  FeedbackFilterOption,
  FeedbackFilterOptions,
  FeedbackFilterSearch,
} from './feedback-page.styles';
import {
  allTimeFeedbackRange,
  customFeedbackDateRange,
  FeedbackDatePreset,
  FeedbackDateRange,
  feedbackDateRangeForPreset,
} from './feedback-date-range';

interface FeedbackFilterOptionValue {
  label: string;
  value: string;
}

interface FeedbackFilterControlProps {
  ariaLabel: string;
  label: string;
  options: FeedbackFilterOptionValue[];
  value: string;
  onChange: (value: string) => void;
  searchable?: boolean;
  disabled?: boolean;
}

export const FeedbackFilterControl = ({
  ariaLabel,
  label,
  options,
  value,
  onChange,
  searchable = false,
  disabled = false,
}: FeedbackFilterControlProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const selectedOption =
    options.find((option) => option.value === value) || options[0];
  const pinnedOption = searchable ? options[0] : undefined;
  const filteredOptions = useMemo(() => {
    if (!searchable) return options;

    const query = searchQuery.trim().toLocaleLowerCase();
    return options
      .slice(1)
      .filter((option) => option.label.toLocaleLowerCase().includes(query));
  }, [options, searchQuery, searchable]);

  const selectOption = (
    option: FeedbackFilterOptionValue,
    handleClose: () => void,
  ) => {
    onChange(option.value);
    setSearchQuery('');
    handleClose();
  };

  const renderOption = (
    option: FeedbackFilterOptionValue,
    handleClose: () => void,
  ) => (
    <FeedbackFilterOption
      key={option.value}
      type="button"
      role="menuitemradio"
      aria-checked={option.value === value}
      $active={option.value === value}
      onClick={() => selectOption(option, handleClose)}>
      {option.label}
      {option.value === value && <FiCheck aria-hidden="true" />}
    </FeedbackFilterOption>
  );

  return (
    <DropdownCustom
      collapseOnClick
      customDropdownWrapperStyles={{
        top: 'calc(100% + 8px)',
        left: 0,
        right: 'auto',
        minWidth: '180px',
        padding: '6px',
      }}
      customComponent={(handleOpen: () => void, open: boolean) => (
        <FeedbackFilterButton
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            if (!open) setSearchQuery('');
            handleOpen();
          }}>
          <span>{label}</span>
          <strong>{selectedOption.label}</strong>
          <FiChevronDown aria-hidden="true" />
        </FeedbackFilterButton>
      )}
      menu={(handleClose: () => void) => (
        <FeedbackFilterMenu role="menu">
          {searchable && (
            <FeedbackFilterSearch>
              <FiSearch aria-hidden="true" />
              <input
                autoFocus
                type="search"
                aria-label="Search people"
                placeholder="Search people"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}>
                  <FiX size={14} aria-hidden="true" />
                </button>
              )}
            </FeedbackFilterSearch>
          )}
          {pinnedOption && renderOption(pinnedOption, handleClose)}
          <FeedbackFilterOptions>
            {filteredOptions.map((option) => renderOption(option, handleClose))}
            {searchable && filteredOptions.length === 0 && (
              <FeedbackFilterEmpty>No people found</FeedbackFilterEmpty>
            )}
          </FeedbackFilterOptions>
        </FeedbackFilterMenu>
      )}
    />
  );
};

interface FeedbackDateControlProps {
  dateRange: FeedbackDateRange;
  setDateRange: (dateRange: FeedbackDateRange) => void;
}

export const FeedbackDateControl = ({
  dateRange,
  setDateRange,
}: FeedbackDateControlProps) => {
  const [open, setOpen] = useState(false);
  const [draftPreset, setDraftPreset] = useState<FeedbackDatePreset>(
    dateRange.preset,
  );
  const [customStart, setCustomStart] = useState(
    dateRange.starts
      ? dayjs(dateRange.starts).format('YYYY-MM-DD')
      : dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
  );
  const [customEnd, setCustomEnd] = useState(
    dateRange.ends
      ? dayjs(dateRange.ends).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD'),
  );
  const [openDateSelect, setOpenDateSelect] = useState<'start' | 'end' | null>(
    null,
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    setOpenDateSelect(null);
  };

  const toggle = () => {
    if (!open) {
      setDraftPreset(dateRange.preset);
      setCustomStart(
        dateRange.preset === 'custom'
          ? dayjs(dateRange.starts).format('YYYY-MM-DD')
          : dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
      );
      setCustomEnd(
        dateRange.preset === 'custom'
          ? dayjs(dateRange.ends).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
      );
    }
    setOpen((current) => !current);
    setOpenDateSelect(null);
  };

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  return (
    <ControlWrap ref={wrapperRef}>
      <ControlButton
        type="button"
        $active={open}
        aria-label="Filter feedback by date"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}>
        <FiCalendar aria-hidden="true" />
        {dateRange.label}
        <FiChevronDown aria-hidden="true" />
      </ControlButton>
      {open && (
        <DatePopover $align="right" role="dialog" aria-label="Select date">
          <PopoverBody>
            <PopoverHeader>
              <strong>Select date</strong>
              <TextButton type="button" onClick={close}>
                Cancel
              </TextButton>
            </PopoverHeader>
            <div role="radiogroup" aria-label="Date range">
              {(
                [
                  ['all', 'All time'],
                  ['30-days', 'Past 30 days'],
                  ['90-days', 'Past 90 days'],
                  ['custom', 'Custom range'],
                ] as Array<[FeedbackDatePreset, string]>
              ).map(([preset, label]) => (
                <FeedbackFilterOption
                  key={preset}
                  type="button"
                  role="radio"
                  aria-checked={draftPreset === preset}
                  $active={draftPreset === preset}
                  onClick={() => {
                    setDraftPreset(preset);
                    setOpenDateSelect(null);
                  }}>
                  {label}
                  {draftPreset === preset && <FiCheck aria-hidden="true" />}
                </FeedbackFilterOption>
              ))}
            </div>
            {draftPreset === 'custom' && (
              <FieldGrid style={{marginTop: 14}}>
                <DateSelectField>
                  <span>Starts</span>
                  <DateInputButton
                    type="button"
                    $open={openDateSelect === 'start'}
                    aria-label="Select start date"
                    aria-haspopup="dialog"
                    aria-expanded={openDateSelect === 'start'}
                    onClick={() =>
                      setOpenDateSelect((current) =>
                        current === 'start' ? null : 'start',
                      )
                    }>
                    <span>
                      {customStart
                        ? dayjs(customStart).format('DD/MM/YYYY')
                        : 'DD/MM/YYYY'}
                    </span>
                    <FiCalendar size={16} aria-hidden="true" />
                  </DateInputButton>
                  {openDateSelect === 'start' && (
                    <DateCalendarMenu>
                      <Calendar
                        value={customStart}
                        disabledAfter={customEnd || undefined}
                        onChange={(date) => {
                          setCustomStart(dayjs(date).format('YYYY-MM-DD'));
                          setOpenDateSelect(null);
                        }}
                      />
                    </DateCalendarMenu>
                  )}
                </DateSelectField>
                <DateSelectField>
                  <span>Ends</span>
                  <DateInputButton
                    type="button"
                    $open={openDateSelect === 'end'}
                    aria-label="Select end date"
                    aria-haspopup="dialog"
                    aria-expanded={openDateSelect === 'end'}
                    onClick={() =>
                      setOpenDateSelect((current) =>
                        current === 'end' ? null : 'end',
                      )
                    }>
                    <span>
                      {customEnd
                        ? dayjs(customEnd).format('DD/MM/YYYY')
                        : 'DD/MM/YYYY'}
                    </span>
                    <FiCalendar size={16} aria-hidden="true" />
                  </DateInputButton>
                  {openDateSelect === 'end' && (
                    <DateCalendarMenu $align="right">
                      <Calendar
                        value={customEnd}
                        disabledBefore={customStart || undefined}
                        onChange={(date) => {
                          setCustomEnd(dayjs(date).format('YYYY-MM-DD'));
                          setOpenDateSelect(null);
                        }}
                      />
                    </DateCalendarMenu>
                  )}
                </DateSelectField>
              </FieldGrid>
            )}
          </PopoverBody>
          <PopoverFooter>
            <TextButton
              type="button"
              $active={dateRange.preset !== 'all'}
              disabled={dateRange.preset === 'all'}
              onClick={() => {
                setDateRange(allTimeFeedbackRange);
                close();
              }}>
              Clear date filter
            </TextButton>
            <SmallPrimaryButton
              type="button"
              disabled={
                draftPreset === 'custom' &&
                (!customStart ||
                  !customEnd ||
                  dayjs(customEnd).isBefore(dayjs(customStart)))
              }
              onClick={() => {
                setDateRange(
                  draftPreset === 'custom'
                    ? customFeedbackDateRange(customStart, customEnd)
                    : feedbackDateRangeForPreset(draftPreset),
                );
                close();
              }}>
              Apply
            </SmallPrimaryButton>
          </PopoverFooter>
        </DatePopover>
      )}
    </ControlWrap>
  );
};
